import styled from 'styled-components'
import { Drawer, Stack, Box } from '@mui/material'
import { motion } from 'framer-motion'
import { useRef } from 'react'
import { useGetAssetById } from '../../../api/contentful/contentfulQueries'
import { CombinedHealthCard } from '../../../api/ester-b2b-api/private/healthPlan/types'
import { parseContenfulImageUrl } from '../../../contentful/utils'
import { theme } from '../../../theme/theme'
import { getHealthCardModuleImageId, DEFAULT_MODULE_IMAGE } from '../../../utils/imageMapper'
import { Close } from '../../icons'
import { HealthPlanCardCollapsible } from './HealthPlanCardCollapsible'
import { Title2 } from '../../styles/Typography'

type Props = {
  card: CombinedHealthCard | null
  isOpen: boolean
  onClose: () => void
}

export const HealthPlanMobileCard = ({ card, isOpen, onClose }: Props) => {
  const assetId = card ? getHealthCardModuleImageId(card.module) : ''
  const { data: imageAsset } = useGetAssetById(assetId)
  const imageUrl = imageAsset ? parseContenfulImageUrl(imageAsset) : DEFAULT_MODULE_IMAGE
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  if (!card) return null

  const scrollToTop = (element: HTMLElement) => {
    if (scrollContainerRef.current) {
      const containerTop = scrollContainerRef.current.getBoundingClientRect().top
      const elementTop = element.getBoundingClientRect().top
      const offsetTop = elementTop - containerTop + scrollContainerRef.current.scrollTop - 50

      scrollContainerRef.current.scrollTo({ top: offsetTop, behavior: 'smooth' })
    }
  }

  return (
    <StyledDrawer anchor="bottom" open={isOpen} onClose={onClose} transitionDuration={400}>
      <CloseButtonWrapper>
        <CloseButton onClick={onClose}>
          <Close size={24} />
        </CloseButton>
      </CloseButtonWrapper>

      <DrawerContent ref={scrollContainerRef}>
        <ImageWrapper $isOpen={isOpen}>
          <FadeUp delay={0.1}>
            <Image src={imageUrl} alt={card.title} />
          </FadeUp>
          <FadeUp delay={0.2}>
            <ImageTitle>{card.title}</ImageTitle>
          </FadeUp>
        </ImageWrapper>

        <FadeUp delay={0.2}>
          <Stack spacing={theme.spacing.tiny} padding={`${theme.spacing.mediumLarge}px ${theme.spacing.tiny}px`}>
            {card.content.map((content, index) => (
              <HealthPlanCardCollapsible
                key={index}
                content={content}
                isExpanded={isOpen}
                onOpen={scrollToTop}
                carePlan={!!card.careLevel}
              />
            ))}
          </Stack>
        </FadeUp>
      </DrawerContent>
    </StyledDrawer>
  )
}

type FadeUpProps = {
  delay?: number
  duration?: number
  style?: React.CSSProperties
  children?: React.ReactNode
} & React.ComponentProps<typeof motion.div>

const FadeUp = ({ delay = 0, duration = 0.4, style, children, ...rest }: FadeUpProps) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration }}
    style={style}
    {...rest}
  >
    {children}
  </motion.div>
)

const ABOVE_SIDEBAR_Z_INDEX = 10000

const StyledDrawer = styled(Drawer)`
  &.MuiDrawer-root {
    z-index: ${ABOVE_SIDEBAR_Z_INDEX};
  }

  .MuiDrawer-paper {
    height: 100%;
    position: relative;
  }
`

const DrawerContent = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  overflow-y: auto;
`

const CloseButtonWrapper = styled(motion.div)`
  position: absolute;
  top: ${theme.spacing.medium}px;
  left: ${theme.spacing.medium}px;
  z-index: 20;
`

const CloseButton = styled.button`
  background-color: ${theme.color.white};
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const ImageWrapper = styled.div<{ $isOpen: boolean }>`
  position: relative;

  ${({ $isOpen }) =>
    $isOpen &&
    `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 100%;
      background: linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 100%);
      pointer-events: none;
    }
  `}
`

const Image = styled.img`
  display: block;
  width: 100%;
  max-height: 250px;
  object-fit: cover;
  object-position: top;
`

const ImageTitle = styled(Title2)`
  position: absolute;
  bottom: ${theme.spacing.mediumLarge}px;
  left: ${theme.spacing.mediumLarge}px;
  color: ${theme.color.white};
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.3);
  margin: 0;
  z-index: 1;
`
